import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Loader, ShieldCheck } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { authManager } from '../services/authManager';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError('El enlace de recuperación no es válido o ya expiró. Solicita uno nuevo.');
      return;
    }

    if (!password || password.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authManager.resetPassword({ token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Error al restablecer la contraseña. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <SEOHead 
          title="Contraseña restablecida - Numerathum"
          description="Tu contraseña fue actualizada correctamente."
          noindex={true}
        />
        <div className="min-h-screen bg-morado/5 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-[#d4af37] text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-3xl">
              ✓
            </div>
            <h2 className="text-2xl font-bold text-morado mb-2">
              ¡Contraseña actualizada!
            </h2>
            <p className="text-gray-600 mb-6">
              Tu contraseña fue restablecida correctamente. Ya puedes iniciar sesión con tus nuevas credenciales.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-block w-full bg-morado text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Ir al inicio de sesión
            </button>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title="Restablecer Contraseña - Numerathum"
        description="Ingresa una nueva contraseña para tu cuenta."
        noindex={true}
      />
      <div className="min-h-screen bg-morado/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <Link to="/" className="inline-block mb-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-morado flex items-center justify-center text-white text-2xl font-bold">
                <ShieldCheck className="w-8 h-8" />
              </div>
            </Link>
            <h1 className="text-3xl font-bold text-morado mb-2">Restablecer Contraseña</h1>
            <p className="text-gray-600">Crea una nueva contraseña segura para tu cuenta</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl p-8 border border-[#d4af37]"
          >
            {!token && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
                El enlace no es válido o ya expiró. Solicita un nuevo correo de recuperación.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                    placeholder="••••••••"
                    minLength={8}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                    placeholder="••••••••"
                    minLength={8}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-morado text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    Guardar Contraseña
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm">
              <Link
                to="/login"
                className="text-gray-600 hover:text-morado"
              >
                ← Volver al inicio de sesión
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}












